import { useState } from 'react';
import clsx from 'clsx';
import DOMPurify from 'dompurify';

type AlertType = 'primary' | 'info' | 'secondary' | 'danger' | 'success' | 'warning' | 'outline';

type AlertProps = {
    type: AlertType;
    messages: React.ReactNode[];
    className?: string;
    allowHtml?: boolean;
};

const typeStyles: Record<AlertType, string> = {
    primary: 'bg-blue-100 text-blue-800 border border-blue-300',
    info: 'bg-cyan-100 text-cyan-800 border border-cyan-300',
    secondary: 'bg-gray-200 text-gray-800 border border-gray-300',
    danger: 'bg-red-100 text-red-800 border border-red-300',
    success: 'bg-green-100 text-green-800 border border-green-300',
    warning: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    outline: 'border border-gray-500 text-gray-800',
};

const sanitizeConfig = {
    ALLOWED_TAGS: ['a', 'strong', 'em', 'span', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'style', 'class'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover'],
};

export const Alert = ({ type, messages, className = '', allowHtml = false }: AlertProps) => {
    const [visible, setVisible] = useState(true);

    if (!visible) return null;

    const renderMessage = (msg: React.ReactNode, index: number) => {
        if (allowHtml && typeof msg === 'string') {
            const sanitizedHtml = DOMPurify.sanitize(msg, sanitizeConfig);
            return <li key={index} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
        }

        return <li key={index}>{msg}</li>;
    };

    return (
        <div className={clsx('relative rounded alert-container', typeStyles[type], className)}>
            <button
                onClick={() => setVisible(false)}
                className="absolute top-1 right-1 text-gray-400 hover:text-gray-800"
            >
                ×
            </button>
            <ul className="space-y-1">{messages.map((msg, index) => renderMessage(msg, index))}</ul>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                    .alert-container li a {
                        color: #3b82f6 !important;
                        text-decoration: underline !important;
                        font-weight: 500 !important;
                    }
                    .alert-container li a:hover {
                        color: #1d4ed8 !important;
                    }
                    .alert-container li strong {
                        font-weight: 600 !important;
                    }
                `,
                }}
            />
        </div>
    );
};

export default Alert;
